<template>
  <a-row :gutter="[16, 8]" class="py-4 px-2 p-md-4 mb-4">
    <a-col :xs="24" :sm="24" :md="8" :lg="8">
      <a-select
        ref="task_category_org_select"
        v-model:value="orgName"
        show-search
        placeholder="Select Organization"
        class="w-100"
        :options="orgOptions"
        :filter-option="true"
        option-filter-prop="label"
        :loading="isLoadingOrganizations"
        @change="handleOrgChange"
      />
    </a-col>
    <a-col :xs="24" :sm="24" :md="16" :lg="16" class="d-flex justify-content-end">
      <a-button
        type="primary"
        :disabled="!orgName"
        @click="showCreateModal"
      >
        <template #icon>
          <plus-outlined />
        </template>
        Add Category
      </a-button>
    </a-col>
    <a-col span="24" class="mt-4">
      <a-table
        bordered
        :data-source="categories"
        :columns="columns"
        :loading="isLoading"
        :scroll="{
          y: '65vh',
          x: true,
        }"
        :pagination="{ position: ['bottomCenter'], pageSize: 15 }"
      >
        <template #title>
          <div class="d-flex">
            <a-typography-title :level="5" class="my-0">
              Task Categories: &nbsp;
              <a-tag color="blue">
                {{ categories.length }}
              </a-tag>
            </a-typography-title>
          </div>
        </template>
        <template #emptyText>
          <div
            style="height: 40vh"
            class="d-flex flex-column align-items-center justify-content-center"
          >
            <folder-outlined style="font-size: 40px" />
            <span class="mt-2">
              {{ orgName ? 'No Task Categories Found!' : 'Please select an organization' }}
            </span>
          </div>
        </template>

        <template #bodyCell="{ column, text, record }">
          <template v-if="column.dataIndex === 'description'">
            {{ text || '-' }}
          </template>

          <template v-if="column.dataIndex === 'created_at'">
            {{ formatDate(text) }}
          </template>

          <template v-if="column.dataIndex === 'actions'">
            <a-space :size="12">
              <a-tooltip title="Edit">
                <a-button type="primary" @click="showEditModal(record)">
                  <template #icon>
                    <edit-outlined />
                  </template>
                </a-button>
              </a-tooltip>
              <a-tooltip :title="record.task_count > 0 ? 'Cannot delete: has associated tasks' : 'Delete'">
                <a-popconfirm
                  title="Are you sure you want to delete this category?"
                  ok-text="Yes"
                  cancel-text="No"
                  :disabled="record.task_count > 0"
                  @confirm="handleDelete(record)"
                >
                  <a-button
                    danger
                    :disabled="record.task_count > 0"
                  >
                    <template #icon>
                      <delete-outlined />
                    </template>
                  </a-button>
                </a-popconfirm>
              </a-tooltip>
            </a-space>
          </template>
        </template>
      </a-table>
    </a-col>
  </a-row>

  <TaskCategoryModal
    :visible="showModal"
    :category="selectedCategory"
    :is-submitting="isSubmitting"
    @close="closeModal"
    @submit="handleSubmit"
  />
</template>

<script>
import {
  DeleteOutlined,
  EditOutlined,
  FolderOutlined,
  PlusOutlined,
} from '@ant-design/icons-vue';
import { mapActions, mapGetters } from 'vuex';
import TaskCategoryService from 'src/services/taskCategory';
import TaskCategoryModal from './TaskCategoryModal.vue';
import dateHelper from '../../../shared/Helpers/dateHelper';

const columns = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    align: 'left',
    width: '20%',
  },
  {
    title: 'Description',
    dataIndex: 'description',
    key: 'description',
    align: 'left',
    width: '35%',
  },
  {
    title: 'Task Count',
    dataIndex: 'task_count',
    key: 'task_count',
    align: 'center',
    width: '10%',
  },
  {
    title: 'Created At',
    dataIndex: 'created_at',
    key: 'created_at',
    align: 'center',
    width: '15%',
  },
  {
    title: 'Actions',
    dataIndex: 'actions',
    key: 'actions',
    align: 'center',
    width: '20%',
  },
];

export default {
  components: {
    DeleteOutlined,
    EditOutlined,
    FolderOutlined,
    PlusOutlined,
    TaskCategoryModal,
  },
  inject: ['toast'],
  data() {
    return {
      columns,
      orgName: null,
      categories: [],
      isLoading: false,
      showModal: false,
      selectedCategory: null,
      isSubmitting: false,
    };
  },
  computed: {
    ...mapGetters(['organizations', 'isLoadingOrganizations']),
    orgOptions() {
      return this.organizations?.map((org) => ({
        value: org.Org_name,
        label: org.org_name_to_display,
      }));
    },
  },
  created() {
    this.getOrganizations();
  },
  mounted() {
    this.populateParams();
  },
  methods: {
    ...mapActions(['getOrganizations']),

    formatDate(dateStr) {
      if (!dateStr) return '-';
      return dateHelper.formatDate(dateStr);
    },

    setRouteQueryParams(query) {
      this.$router.replace({
        name: this.$route.name,
        query,
      });
    },

    populateParams() {
      const { organization } = this.$route.query;
      if (organization) {
        this.orgName = organization;
        this.fetchCategories();
      }
    },

    handleOrgChange() {
      this.$refs.task_category_org_select.blur();
      this.setRouteQueryParams({ organization: this.orgName });
      this.fetchCategories();
    },

    async fetchCategories() {
      if (!this.orgName) {
        this.categories = [];
        return;
      }

      this.isLoading = true;
      const [error, data] = await TaskCategoryService.getTaskCategories(this.orgName);
      this.isLoading = false;

      if (error) {
        this.toast.error('Error fetching task categories');
        this.categories = [];
        return;
      }

      this.categories = data || [];
    },

    showCreateModal() {
      this.selectedCategory = null;
      this.showModal = true;
    },

    showEditModal(record) {
      this.selectedCategory = { ...record };
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.selectedCategory = null;
    },

    async handleSubmit(formData) {
      this.isSubmitting = true;

      if (this.selectedCategory) {
        await this.updateCategory(formData);
      } else {
        await this.createCategory(formData);
      }

      this.isSubmitting = false;
    },

    async createCategory(formData) {
      const payload = {
        ...formData,
        organization: this.orgName,
      };

      const [error, response] = await TaskCategoryService.createTaskCategory(payload);

      if (error) {
        const errorMsg = error?.response?.data?.error || error?.response?.data?.name?.[0] || 'Error creating task category';
        this.toast.error(errorMsg);
        return;
      }

      this.toast.success('Task category created successfully');
      this.closeModal();
      this.fetchCategories();
    },

    async updateCategory(formData) {
      const [error, response] = await TaskCategoryService.updateTaskCategory(
        this.selectedCategory.id,
        formData
      );

      if (error) {
        const errorMsg = error?.response?.data?.error || error?.response?.data?.name?.[0] || 'Error updating task category';
        this.toast.error(errorMsg);
        return;
      }

      this.toast.success('Task category updated successfully');
      this.closeModal();
      this.fetchCategories();
    },

    async handleDelete(record) {
      this.isLoading = true;
      const [error] = await TaskCategoryService.deleteTaskCategory(record.id);
      this.isLoading = false;

      if (error) {
        const errorMsg = error?.response?.data?.error || 'Error deleting task category';
        this.toast.error(errorMsg);
        return;
      }

      this.toast.success('Task category deleted successfully');
      this.fetchCategories();
    },
  },
};
</script>
